import React from "react";
import { LANDING } from "@/constants/testIds";

const Hero = ({ onClaim, spots = { total: 10, claimed: 0, remaining: 10 } }) => {
  const sold_out = spots.remaining <= 0;
  const progressPct = Math.min(100, Math.round((spots.claimed / spots.total) * 100));
  return (
    <section
      data-testid={LANDING.heroSection}
      className="relative overflow-hidden pt-28 md:pt-32 pb-20 md:pb-28 px-6 md:px-12"
    >
      {/* Decorative blurred fiesta orbs */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#FF8200]/20 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-[#00B2A9]/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🏀</span>
            <span className="font-black tracking-tight text-lg">Haus of Chi</span>
          </div>
          <span className="hidden md:inline text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">
            San Antonio · Texas
          </span>
        </div>

        <div
          data-testid={LANDING.urgencyBanner}
          className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white text-[11px] md:text-xs font-bold tracking-[0.2em] uppercase py-2.5 px-5 rounded-full mb-8"
        >
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#EF426F] opacity-75 animate-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-[#EF426F]" />
          </span>
          <span>
            {sold_out
              ? "All 10 spots claimed — Waitlist only"
              : `Only ${spots.remaining} of ${spots.total} spots left`}
          </span>
        </div>

        <h1
          data-testid={LANDING.heroHeadline}
          className="text-[40px] leading-[0.95] sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter max-w-5xl"
        >
          Don't let your competitors{" "}
          <span className="bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-transparent bg-clip-text">
            score all the customers
          </span>
          .
        </h1>

        <p className="mt-6 md:mt-8 text-lg md:text-xl text-zinc-600 max-w-3xl font-medium leading-relaxed">
          While the Spurs have the home-court advantage, your business has the
          opportunity to turn Finals excitement into{" "}
          <span className="text-[#0A0A0A] font-bold">more visibility</span>,{" "}
          <span className="text-[#0A0A0A] font-bold">more customers</span>, and{" "}
          <span className="text-[#0A0A0A] font-bold">more sales</span>.
        </p>

        <p className="mt-4 text-base md:text-lg text-zinc-500 max-w-3xl leading-relaxed">
          Thousands of fans will be searching for places to eat, shop, celebrate, stay,
          book, and spend money throughout San Antonio during the Finals. Let's make sure
          they find <span className="font-bold text-[#0A0A0A]">YOUR business</span> first.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={onClaim}
            data-testid={LANDING.heroCta}
            className="rounded-full bg-[#0A0A0A] text-white font-bold px-8 py-4 text-sm md:text-base tracking-wider uppercase transition-all hover:shadow-[0_10px_40px_-10px_#EF426F] hover:scale-[1.02] active:scale-[0.98]"
          >
            🏀 Claim My Finals Spot
          </button>
          <div className="text-xs md:text-sm text-zinc-500">
            <span className="font-bold text-[#0A0A0A]">No Calendly.</span> No application. Just Stripe checkout.
          </div>
        </div>

        {/* Live spots progress */}
        <div className="mt-12 md:mt-14 max-w-xl">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              Spots Claimed
            </span>
            <span className="text-sm font-black tracking-tight">
              <span className="bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-transparent bg-clip-text">
                {spots.claimed}
              </span>
              <span className="text-zinc-400"> / {spots.total}</span>
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-zinc-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] transition-all duration-700"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Stat strip */}
        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-black/10 pt-10">
          {[
            { label: "Spots remaining", value: String(spots.remaining) },
            { label: "Packages", value: "3" },
            { label: "Location", value: "San Antonio" },
            { label: "Lead time", value: "Now" },
          ].map((s) => (
            <div key={s.label}>
              <div className="text-2xl md:text-4xl font-black tracking-tight">{s.value}</div>
              <div className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
